import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/src/components/ui/table';
import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ClientDetailBundle } from '@repo/shared-types';
import { useExtrasAccess } from '../../../hooks/useExtrasAccess';
import { useUpgradeDialogStore } from '../../../stores/upgradeDialogStore';
import { InvoiceStatusBadge } from '../../../lib/invoiceStatus';
import { EmptyState } from '../../../components/EmptyState';

// Note: OVERDUE is derived, never a stored invoice status (see invoiceStatus.ts /
// Polish B), so it can't appear as `inv.status` here. `ClientDetailBundle`'s
// invoice shape (packages/shared-types/src/client.ts) doesn't carry the derived
// `isOverdue` flag the way the full `Invoice` type does (see InvoicesPage /
// InvoiceDetailPage), so this table can't drive overdue styling without a
// broader type/API change. Status colors come from the shared InvoiceStatusBadge.

function formatVND(amount: number): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('vi-VN');
}

interface InvoicesTabProps {
  invoices: ClientDetailBundle['invoices'];
  clientId: string;
}

export function InvoicesTab({ invoices, clientId }: InvoicesTabProps) {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();
  // Extras three-state: enabled (planAllows && orgSells) -> normal button;
  // !planAllows -> lock icon, opens upgrade dialog; planAllows && !orgSells ->
  // hidden (org's own choice, not an upsell). See useExtrasAccess.
  const { planAllows, enabled: extrasEnabled } = useExtrasAccess();
  const openUpgradeDialog = useUpgradeDialogStore((s) => s.open);

  if (invoices.length === 0) {
    return (
      <EmptyState
        kind="teach"
        icon={FileText}
        title={t('invoices.empty')}
        body={t('invoices.emptyBody')}
        // EmptyState's action has no icon slot, so the locked variant loses the
        // padlock here and keeps it in the header, where the same action lives.
        action={
          extrasEnabled
            ? { label: t('invoices.createFirst'), to: `/invoices/new?clientId=${clientId}` }
            : !planAllows
              ? { label: t('invoices.createFirst'), onClick: () => openUpgradeDialog('extras') }
              : undefined
        }
      />
    );
  }

  return (
    <div className="rounded-md border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('invoices.code')}</TableHead>
            <TableHead className="text-right">{t('invoices.amount')}</TableHead>
            <TableHead>{t('invoices.status')}</TableHead>
            <TableHead>{t('invoices.due')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((inv) => (
            <TableRow
              key={inv.id}
              className="cursor-pointer"
              onClick={() => navigate(`/invoices/${inv.id}`)}
            >
              <TableCell className="font-mono text-sm">{inv.invoiceCode}</TableCell>
              <TableCell className="text-right font-medium">{formatVND(inv.totalAmount)}</TableCell>
              <TableCell>
                <InvoiceStatusBadge status={inv.status} />
              </TableCell>
              <TableCell className="text-sm text-surface-600">{formatDate(inv.dueDate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
